import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Spinner } from '../../../shared/components';
import { useAuth } from '../../auth/context/AuthContext';
import { PermissionCodes } from '../../auth/permissions';
import { listChannelAccounts } from '../../connectors/services/channelAccountService';
import type { ChannelAccount } from '../../connectors/types/channelAccount';
import type {
  ConversationInteractiveButtons,
  ConversationInteractiveList,
} from '../../conversations/types/Conversation';
import { useToast } from '../../notifications';
import {
  createCommentAutomation,
  deleteCommentAutomation,
  listCommentAutomations,
  setCommentAutomationEnabled,
  updateCommentAutomation,
} from '../services/commentAutomationService';
import {
  CommentAutomationAction,
  CommentAutomationBodySource,
  CommentAutomationChannel,
  CommentAutomationInteractiveMode,
  CommentAutomationRule,
  emptyAction,
  emptyInteractiveButton,
  emptyInteractiveButtons,
  emptyInteractiveList,
  emptyInteractiveRow,
  emptyInteractiveSection,
  summarizeInteractiveAction,
  toCommentAutomationActionRequest,
  toInteractiveButtonsDraft,
  toInteractiveListDraft,
  validateInteractiveAction,
} from '../types/CommentAutomation';
import {
  getInteractiveButtonMaxCountForChannel,
  isInteractiveButtonAddDisabled,
} from '../../../shared/utils/interactiveButtonLimits';
import { CommentAutomationPostPicker } from './CommentAutomationPostPicker';
import { AutomationActionFailurePicker } from '../../automations/components/AutomationActionFailurePicker';
import { AutomationConditionBuilder } from '../../automations/components/AutomationConditionBuilder';
import type { AutomationActionFailureMode, AutomationCondition, AutomationConditionLogic } from '../../automations/types/AutomationCondition';
import {
  emptyKeywordCondition,
  firstKeywordMatchMode,
  flattenConditionKeywords,
  hasValidAutomationConditions,
  toAutomationConditionRequest,
} from '../../automations/types/AutomationCondition';
import styles from './CommentAutomationsPage.module.css';

type EditorMode = 'create' | 'edit';

type EditorState = {
  mode: EditorMode;
  id?: string;
  name: string;
  channel: CommentAutomationChannel;
  channelAccountId: string;
  isEnabled: boolean;
  priority: number;
  conditionLogic: AutomationConditionLogic;
  conditions: AutomationCondition[];
  onActionFailure: AutomationActionFailureMode;
  externalContentId: string;
  actions: CommentAutomationAction[];
};

const emptyEditor = (): EditorState => ({
  mode: 'create',
  name: '',
  channel: 'instagram',
  channelAccountId: '',
  isEnabled: true,
  priority: 100,
  conditionLogic: 'any',
  conditions: [emptyKeywordCondition('equals', ['+'])],
  onActionFailure: 'continue',
  externalContentId: '',
  actions: [emptyAction('public_comment_reply'), emptyAction('private_reply')],
});

const getInteractiveMode = (action: CommentAutomationAction): CommentAutomationInteractiveMode =>
  action.interactiveMode
  ?? (action.interactiveButtons && !action.interactiveList ? 'buttons' : 'list');

const buildBodySourcePatch = (
  current: CommentAutomationAction,
  bodySource: CommentAutomationBodySource,
): Partial<CommentAutomationAction> => {
  if (bodySource === 'interactive') {
    return {
      bodySource,
      templateText: null,
      fallbackTemplateText: null,
      interactiveMode: current.interactiveMode ?? 'list',
      interactiveList: toInteractiveListDraft(current.interactiveList),
      interactiveButtons: toInteractiveButtonsDraft(current.interactiveButtons),
    };
  }

  if (bodySource === 'ai') {
    return {
      bodySource,
      templateText: null,
      fallbackTemplateText: current.fallbackTemplateText ?? '',
      instruction: current.instruction ?? '',
      interactiveMode: null,
      interactiveList: null,
      interactiveButtons: null,
    };
  }

  return {
    bodySource: 'template',
    templateText: current.templateText ?? '',
    fallbackTemplateText: null,
    interactiveMode: null,
    interactiveList: null,
    interactiveButtons: null,
  };
};

export const CommentAutomationsPage: React.FC = () => {
  const { t } = useTranslation(['commentAutomations', 'common', 'automations']);
  const { showToast } = useToast();
  const { hasPermission } = useAuth();
  const canManage = hasPermission(PermissionCodes.conversationsManage);

  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [rules, setRules] = React.useState<CommentAutomationRule[]>([]);
  const [channelFilter, setChannelFilter] = React.useState<CommentAutomationChannel | ''>('');
  const [enabledFilter, setEnabledFilter] = React.useState<'all' | 'true' | 'false'>('all');
  const [editor, setEditor] = React.useState<EditorState | null>(null);
  const [saving, setSaving] = React.useState(false);
  const [saveError, setSaveError] = React.useState<string | null>(null);
  const [instagramAccounts, setInstagramAccounts] = React.useState<ChannelAccount[]>([]);
  const [messengerAccounts, setMessengerAccounts] = React.useState<ChannelAccount[]>([]);

  const loadRules = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await listCommentAutomations({
        channel: channelFilter || undefined,
        isEnabled: enabledFilter === 'all' ? undefined : enabledFilter === 'true',
        page: 1,
        pageSize: 100,
      });
      setRules(result.items);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('loadFailed'));
      setRules([]);
    } finally {
      setLoading(false);
    }
  }, [channelFilter, enabledFilter, t]);

  React.useEffect(() => {
    void loadRules();
  }, [loadRules]);

  React.useEffect(() => {
    let cancelled = false;
    void Promise.all([
      listChannelAccounts('Instagram').catch(() => ({ items: [] as ChannelAccount[] })),
      listChannelAccounts('Messenger').catch(() => ({ items: [] as ChannelAccount[] })),
    ]).then(([instagram, messenger]) => {
      if (cancelled) {
        return;
      }
      setInstagramAccounts(instagram.items);
      setMessengerAccounts(messenger.items);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const accountOptions = editor?.channel === 'messenger' ? messengerAccounts : instagramAccounts;

  const openCreate = () => {
    setSaveError(null);
    setEditor(emptyEditor());
  };

  const openEdit = (rule: CommentAutomationRule) => {
    setSaveError(null);
    setEditor({
      mode: 'edit',
      id: rule.id,
      name: rule.name,
      channel: rule.channel,
      channelAccountId: rule.channelAccountId ?? '',
      isEnabled: rule.isEnabled,
      priority: rule.priority,
      conditionLogic: rule.conditionLogic,
      conditions: rule.conditions.length > 0 ? rule.conditions : [emptyKeywordCondition(rule.matchMode, rule.keywords)],
      onActionFailure: rule.onActionFailure,
      externalContentId: rule.externalContentId ?? '',
      actions: rule.actions.length > 0
        ? rule.actions.map((action) => (
          action.bodySource === 'interactive'
            ? {
              ...action,
              interactiveMode: getInteractiveMode(action),
              interactiveList: toInteractiveListDraft(action.interactiveList),
              interactiveButtons: toInteractiveButtonsDraft(action.interactiveButtons),
            }
            : { ...action }
        ))
        : [emptyAction()],
    });
  };

  const closeEditor = () => {
    setEditor(null);
    setSaveError(null);
  };

  const updateAction = (index: number, patch: Partial<CommentAutomationAction>) => {
    setEditor((current) => {
      if (!current) {
        return current;
      }
      const actions = current.actions.map((action, actionIndex) =>
        actionIndex === index ? { ...action, ...patch } : action);
      return { ...current, actions };
    });
  };

  const updateInteractiveList = (
    index: number,
    updater: (list: ConversationInteractiveList) => ConversationInteractiveList,
  ) => {
    setEditor((current) => {
      if (!current) {
        return current;
      }
      const actions = current.actions.map((action, actionIndex) => {
        if (actionIndex !== index) {
          return action;
        }
        return {
          ...action,
          interactiveList: updater(toInteractiveListDraft(action.interactiveList)),
        };
      });
      return { ...current, actions };
    });
  };

  const updateInteractiveButtons = (
    index: number,
    updater: (buttons: ConversationInteractiveButtons) => ConversationInteractiveButtons,
  ) => {
    setEditor((current) => {
      if (!current) {
        return current;
      }
      const actions = current.actions.map((action, actionIndex) => {
        if (actionIndex !== index) {
          return action;
        }
        return {
          ...action,
          interactiveButtons: updater(toInteractiveButtonsDraft(action.interactiveButtons)),
        };
      });
      return { ...current, actions };
    });
  };

  const addAction = () => {
    setEditor((current) => {
      if (!current) {
        return current;
      }
      return {
        ...current,
        actions: [...current.actions, emptyAction('private_reply')],
      };
    });
  };

  const removeAction = (index: number) => {
    setEditor((current) => {
      if (!current) {
        return current;
      }
      const actions = current.actions.filter((_, actionIndex) => actionIndex !== index);
      return {
        ...current,
        actions: actions.length > 0 ? actions : [emptyAction()],
      };
    });
  };

  const handleSave = async () => {
    if (!editor) {
      return;
    }

    const name = editor.name.trim();
    if (!name || !hasValidAutomationConditions(editor.conditions) || editor.actions.length === 0) {
      setSaveError(t('fieldsRequired'));
      return;
    }

    for (const action of editor.actions) {
      if (action.type === 'public_comment_reply' && action.bodySource !== 'ai' && !action.templateText?.trim()) {
        setSaveError(t('publicReplyRequiresTemplate'));
        return;
      }
      if (action.type === 'private_reply' && action.bodySource === 'template' && !action.templateText?.trim()) {
        setSaveError(t('privateTemplateRequired'));
        return;
      }
      if (action.bodySource === 'ai' && !action.fallbackTemplateText?.trim()) {
        setSaveError(t('aiFallbackRequired'));
        return;
      }
      if (action.type === 'private_reply' && action.bodySource === 'interactive') {
        const interactiveError = validateInteractiveAction(action);
        if (interactiveError) {
          setSaveError(t(interactiveError));
          return;
        }
      }
    }

    const request = {
      name,
      channel: editor.channel,
      channelAccountId: editor.channelAccountId.trim() || null,
      isEnabled: editor.isEnabled,
      priority: Number.isFinite(editor.priority) ? editor.priority : 100,
      matchMode: firstKeywordMatchMode(editor.conditions, 'equals'),
      keywords: flattenConditionKeywords(editor.conditions),
      conditionLogic: editor.conditionLogic,
      conditions: editor.conditions.map(toAutomationConditionRequest),
      onActionFailure: editor.onActionFailure,
      externalContentId: editor.externalContentId.trim() || null,
      actions: editor.actions.map(toCommentAutomationActionRequest),
    };

    setSaving(true);
    setSaveError(null);
    try {
      if (editor.mode === 'create') {
        await createCommentAutomation(request);
        showToast(t('created'), 'success');
      } else if (editor.id) {
        await updateCommentAutomation(editor.id, request);
        showToast(t('updated'), 'success');
      }
      closeEditor();
      await loadRules();
    } catch (err) {
      const message = err instanceof Error ? err.message : t('actionFailed');
      setSaveError(message);
      showToast(message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleEnabled = async (rule: CommentAutomationRule) => {
    if (!canManage) {
      return;
    }
    try {
      await setCommentAutomationEnabled(rule.id, !rule.isEnabled);
      showToast(rule.isEnabled ? t('ruleDisabled') : t('ruleEnabled'), 'success');
      await loadRules();
    } catch (err) {
      showToast(err instanceof Error ? err.message : t('actionFailed'), 'error');
    }
  };

  const handleDelete = async (rule: CommentAutomationRule) => {
    if (!canManage) {
      return;
    }
    const confirmed = window.confirm(t('deleteConfirm', { name: rule.name }));
    if (!confirmed) {
      return;
    }
    try {
      await deleteCommentAutomation(rule.id);
      showToast(t('deleted'), 'success');
      if (editor?.id === rule.id) {
        closeEditor();
      }
      await loadRules();
    } catch (err) {
      showToast(err instanceof Error ? err.message : t('actionFailed'), 'error');
    }
  };

  const describeAction = (action: CommentAutomationAction): string => {
    if (action.type === 'public_comment_reply') {
      return action.bodySource === 'ai' ? t('flowPublicReplyAi') : t('flowPublicReply');
    }
    if (action.bodySource === 'ai') {
      return t('flowAiDm');
    }
    if (action.bodySource === 'interactive') {
      const summary = summarizeInteractiveAction(action);
      if (summary?.mode === 'buttons') {
        return t('flowInteractiveButtons', { count: summary.optionCount });
      }
      return t('flowInteractiveList', { count: summary?.optionCount ?? 0 });
    }
    return t('flowDm');
  };

  const renderActionSummary = (rule: CommentAutomationRule) => {
    const interactiveSummaries = rule.actions
      .map((action) => summarizeInteractiveAction(action))
      .filter((summary): summary is NonNullable<typeof summary> => Boolean(summary));

    if (interactiveSummaries.length === 0) {
      return t('actionsCount', { count: rule.actions.length });
    }

    const first = interactiveSummaries[0];
    const label = first.mode === 'buttons'
      ? t('interactiveButtonsSummary', { count: first.optionCount })
      : t('interactiveListSummary', { count: first.optionCount });

    if (interactiveSummaries.length === 1 && rule.actions.length === 1) {
      return label;
    }

    return `${t('actionsCount', { count: rule.actions.length })} · ${label}`;
  };

  return (
    <div className={styles.page}>
      <section className={styles.hero}>
        <div className={styles.heroCopy}>
          <p className={styles.heroEyebrow}>{t('heroEyebrow')}</p>
          <h3 className={styles.heroTitle}>{t('heroTitle')}</h3>
          <p className={styles.pageIntro}>{t('subtitle')}</p>
          <div className={styles.relatedLinks}>
            <Link className={styles.relatedChip} to="/admin/connectors?tab=instagram">
              {t('relatedConnectorsCta')}
            </Link>
            <Link className={styles.relatedChip} to="/admin/companies?tab=chatbot">
              {t('relatedAssistantCta')}
            </Link>
          </div>
        </div>
        {canManage ? (
          <div className={styles.heroActions}>
            <button
              type="button"
              className="btn btn-primary"
              onClick={openCreate}
              disabled={Boolean(editor)}
            >
              {t('createRule')}
            </button>
          </div>
        ) : null}
      </section>

      <section className={styles.toolbar}>
        <div className={styles.filters}>
          <label className={styles.filterField}>
            {t('channel')}
            <select
              className="text-input"
              value={channelFilter}
              onChange={(event) => setChannelFilter(event.target.value as CommentAutomationChannel | '')}
            >
              <option value="">{t('allChannels')}</option>
              <option value="instagram">{t('channelInstagram')}</option>
              <option value="messenger">{t('channelMessenger')}</option>
            </select>
          </label>
          <label className={styles.filterField}>
            {t('status')}
            <select
              className="text-input"
              value={enabledFilter}
              onChange={(event) => setEnabledFilter(event.target.value as 'all' | 'true' | 'false')}
            >
              <option value="all">{t('allStatuses')}</option>
              <option value="true">{t('enabledOnly')}</option>
              <option value="false">{t('disabledOnly')}</option>
            </select>
          </label>
        </div>
        {!loading && !error ? (
          <div className={styles.rulesMeta}>
            {t('rulesCount', { count: rules.length })}
          </div>
        ) : null}
      </section>

      {editor && canManage ? (
        <section className={`${styles.sectionCard} ${styles.editorCard}`}>
          <div className={styles.sectionHeader}>
            <div>
              <h3 className={styles.sectionTitle}>
                {editor.mode === 'create' ? t('createRule') : t('editRule')}
              </h3>
              <p className={styles.sectionKicker}>{t('editorHint')}</p>
            </div>
          </div>

          <div className={styles.editorBlock} style={{ marginTop: 0, paddingTop: 0, borderTop: 'none' }}>
            <h4 className={styles.editorBlockTitle}>
              <span className={styles.stepPill}>1</span>
              {t('whenTitle')}
            </h4>
            <p className={styles.hint}>{t('whenHint')}</p>
            <div className={styles.formGrid} style={{ marginTop: '0.85rem' }}>
              <label className={styles.field}>
                {t('name')}
                <input
                  className="text-input"
                  value={editor.name}
                  onChange={(event) => setEditor({ ...editor, name: event.target.value })}
                />
              </label>
              <label className={styles.field}>
                {t('channel')}
                <select
                  className="text-input"
                  value={editor.channel}
                  onChange={(event) => setEditor({
                    ...editor,
                    channel: event.target.value as CommentAutomationChannel,
                    channelAccountId: '',
                    externalContentId: '',
                  })}
                >
                  <option value="instagram">{t('channelInstagram')}</option>
                  <option value="messenger">{t('channelMessenger')}</option>
                </select>
              </label>
              <label className={styles.field}>
                {t('channelAccount')}
                <select
                  className="text-input"
                  value={editor.channelAccountId}
                  onChange={(event) => setEditor({
                    ...editor,
                    channelAccountId: event.target.value,
                    externalContentId: '',
                  })}
                >
                  <option value="">{t('allAccounts')}</option>
                  {accountOptions.map((account) => (
                    <option key={account.id} value={account.id}>
                      {account.displayName || account.id}
                    </option>
                  ))}
                </select>
                <p className={styles.hint}>{t('channelAccountHint')}</p>
              </label>
              <label className={styles.field}>
                {t('priority')}
                <input
                  className="text-input"
                  type="number"
                  value={editor.priority}
                  onChange={(event) => setEditor({ ...editor, priority: Number(event.target.value) })}
                />
                <p className={styles.hint}>{t('priorityHint')}</p>
              </label>
              <label className={styles.field}>
                {t('enabledField')}
                <select
                  className="text-input"
                  value={editor.isEnabled ? 'true' : 'false'}
                  onChange={(event) => setEditor({ ...editor, isEnabled: event.target.value === 'true' })}
                >
                  <option value="true">{t('yes')}</option>
                  <option value="false">{t('no')}</option>
                </select>
              </label>
              <AutomationConditionBuilder
                conditionLogic={editor.conditionLogic}
                conditions={editor.conditions}
                disabled={!canManage || saving}
                onLogicChange={(conditionLogic) => setEditor({ ...editor, conditionLogic })}
                onConditionsChange={(conditions) => setEditor({ ...editor, conditions })}
              />
              <div className={`${styles.field} ${styles.fieldWide}`}>
                <CommentAutomationPostPicker
                  channel={editor.channel}
                  channelAccountId={editor.channelAccountId || null}
                  value={editor.externalContentId}
                  disabled={!canManage || saving}
                  onChange={(externalContentId) => setEditor({
                    ...editor,
                    externalContentId,
                  })}
                />
              </div>
            </div>
          </div>

          <div className={styles.editorBlock}>
            <h4 className={styles.editorBlockTitle}>
              <span className={styles.stepPill}>2</span>
              {t('actionsTitle')}
            </h4>
            <p className={styles.hint}>{t('actionsHint')}</p>
            <AutomationActionFailurePicker
              value={editor.onActionFailure}
              disabled={!canManage || saving}
              onChange={(onActionFailure) => setEditor({ ...editor, onActionFailure })}
            />
            <div className={styles.actionList} style={{ marginTop: '0.85rem' }}>
              {editor.actions.map((action, index) => {
                const interactiveMode = getInteractiveMode(action);
                const listDraft = toInteractiveListDraft(action.interactiveList);
                const buttonsDraft = toInteractiveButtonsDraft(action.interactiveButtons);
                const interactiveButtonMaxCount = getInteractiveButtonMaxCountForChannel(editor.channel);
                const isButtonAddDisabled = isInteractiveButtonAddDisabled(
                  buttonsDraft.buttons.length,
                  interactiveButtonMaxCount,
                );

                return (
                  <div key={`action-${index}`} className={styles.actionCard}>
                    <div className={styles.actionHeader}>
                      <div className={styles.actionHeaderLeft}>
                        <span className={styles.actionIndex}>{index + 1}</span>
                        <strong>{t('actionLabel', { index: index + 1 })}</strong>
                        <span
                          className={`${styles.actionTypeBadge} ${
                            action.type === 'public_comment_reply'
                              ? styles.actionTypeBadgePublic
                              : styles.actionTypeBadgePrivate
                          }`}
                        >
                          {action.type === 'public_comment_reply' ? t('actionPublicReply') : t('actionPrivateReply')}
                        </span>
                      </div>
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        onClick={() => removeAction(index)}
                        disabled={editor.actions.length <= 1}
                      >
                        {t('removeAction')}
                      </button>
                    </div>
                    <div className={styles.formGrid}>
                      <label className={styles.field}>
                        {t('actionType')}
                        <select
                          className="text-input"
                          value={action.type}
                          onChange={(event) => {
                            const nextType = event.target.value as CommentAutomationAction['type'];
                            if (nextType === 'public_comment_reply') {
                              const nextSource = action.bodySource === 'ai' ? 'ai' : 'template';
                              updateAction(index, {
                                type: nextType,
                                ...buildBodySourcePatch(action, nextSource),
                                templateText: action.templateText || 'Detailed info sent to your DM.',
                              });
                              return;
                            }
                            updateAction(index, {
                              type: nextType,
                              ...buildBodySourcePatch(action, action.bodySource === 'interactive' ? 'interactive' : action.bodySource),
                            });
                          }}
                        >
                          <option value="public_comment_reply">{t('actionPublicReply')}</option>
                          <option value="private_reply">{t('actionPrivateReply')}</option>
                        </select>
                      </label>
                      <label className={styles.field}>
                        {t('bodySource')}
                        <select
                          className="text-input"
                          value={action.bodySource === 'interactive' && action.type === 'public_comment_reply' ? 'template' : action.bodySource}
                          onChange={(event) => updateAction(
                            index,
                            buildBodySourcePatch(
                              action,
                              event.target.value as CommentAutomationBodySource,
                            ),
                          )}
                        >
                          <option value="template">{t('bodyTemplate')}</option>
                          <option value="ai">{t('bodyAi')}</option>
                          {action.type === 'private_reply' ? (
                            <option value="interactive">{t('bodyInteractive')}</option>
                          ) : null}
                        </select>
                      </label>
                      {action.bodySource === 'template' ? (
                        <label className={`${styles.field} ${styles.fieldWide}`}>
                          {t('templateText')}
                          <textarea
                            className="text-input"
                            rows={3}
                            value={action.templateText ?? ''}
                            onChange={(event) => updateAction(index, { templateText: event.target.value })}
                          />
                        </label>
                      ) : null}
                      {action.bodySource === 'ai' ? (
                        <>
                          <label className={`${styles.field} ${styles.fieldWide}`}>
                            {t('aiInstruction')}
                            <textarea
                              className="text-input"
                              rows={3}
                              maxLength={1000}
                              value={action.instruction ?? ''}
                              placeholder={t('aiInstructionPlaceholder')}
                              onChange={(event) => updateAction(index, { instruction: event.target.value })}
                            />
                            <p className={styles.hint}>{t('aiInstructionHint')}</p>
                          </label>
                          <label className={`${styles.field} ${styles.fieldWide}`}>
                            {t('fallbackTemplateText')}
                            <textarea
                              className="text-input"
                              rows={3}
                              value={action.fallbackTemplateText ?? ''}
                              onChange={(event) => updateAction(index, { fallbackTemplateText: event.target.value })}
                            />
                            <p className={styles.hint}>{t('fallbackHint')}</p>
                          </label>
                        </>
                      ) : null}
                      {action.type === 'private_reply' && action.bodySource === 'interactive' ? (
                        <div className={`${styles.field} ${styles.fieldWide}`}>
                          <p className={styles.hint}>{t('interactiveHint')}</p>
                          <div className={styles.interactiveModeTabs} role="tablist" aria-label={t('interactiveMode')}>
                            <button
                              type="button"
                              className={`${styles.interactiveModeTab} ${interactiveMode === 'list' ? styles.interactiveModeTabActive : ''}`}
                              onClick={() => updateAction(index, {
                                interactiveMode: 'list',
                                interactiveList: toInteractiveListDraft(action.interactiveList ?? emptyInteractiveList()),
                              })}
                            >
                              {t('interactiveModeList')}
                            </button>
                            <button
                              type="button"
                              className={`${styles.interactiveModeTab} ${interactiveMode === 'buttons' ? styles.interactiveModeTabActive : ''}`}
                              onClick={() => updateAction(index, {
                                interactiveMode: 'buttons',
                                interactiveButtons: toInteractiveButtonsDraft(action.interactiveButtons ?? emptyInteractiveButtons()),
                              })}
                            >
                              {t('interactiveModeButtons')}
                            </button>
                          </div>

                          {interactiveMode === 'list' ? (
                            <div className={styles.interactiveEditor}>
                              <label className={styles.field}>
                                {t('interactiveBody')}
                                <textarea
                                  className="text-input"
                                  rows={2}
                                  value={listDraft.bodyText}
                                  placeholder={t('interactiveBodyPlaceholder')}
                                  onChange={(event) => updateInteractiveList(index, (list) => ({
                                    ...list,
                                    bodyText: event.target.value,
                                  }))}
                                />
                              </label>
                              <label className={styles.field}>
                                {t('interactiveButton')}
                                <input
                                  className="text-input"
                                  value={listDraft.buttonText}
                                  placeholder={t('interactiveButtonPlaceholder')}
                                  onChange={(event) => updateInteractiveList(index, (list) => ({
                                    ...list,
                                    buttonText: event.target.value,
                                  }))}
                                />
                              </label>
                              <div className={styles.interactiveOptionalGrid}>
                                <label className={styles.field}>
                                  {t('interactiveHeader')}
                                  <input
                                    className="text-input"
                                    value={listDraft.headerText ?? ''}
                                    placeholder={t('interactiveHeaderPlaceholder')}
                                    onChange={(event) => updateInteractiveList(index, (list) => ({
                                      ...list,
                                      headerText: event.target.value,
                                    }))}
                                  />
                                </label>
                                <label className={styles.field}>
                                  {t('interactiveFooter')}
                                  <input
                                    className="text-input"
                                    value={listDraft.footerText ?? ''}
                                    placeholder={t('interactiveFooterPlaceholder')}
                                    onChange={(event) => updateInteractiveList(index, (list) => ({
                                      ...list,
                                      footerText: event.target.value,
                                    }))}
                                  />
                                </label>
                              </div>

                              {listDraft.sections.map((section, sectionIndex) => (
                                <div key={`section-${index}-${sectionIndex}`} className={styles.interactiveSectionCard}>
                                  <div className={styles.actionHeader}>
                                    <strong>{t('interactiveSection', { index: sectionIndex + 1 })}</strong>
                                    <button
                                      type="button"
                                      className="btn btn-secondary btn-sm"
                                      disabled={listDraft.sections.length <= 1}
                                      onClick={() => updateInteractiveList(index, (list) => ({
                                        ...list,
                                        sections: list.sections.filter((_, i) => i !== sectionIndex),
                                      }))}
                                    >
                                      {t('removeSection')}
                                    </button>
                                  </div>
                                  <label className={styles.field}>
                                    {t('interactiveSectionTitle')}
                                    <input
                                      className="text-input"
                                      value={section.title ?? ''}
                                      placeholder={t('interactiveSectionPlaceholder')}
                                      onChange={(event) => updateInteractiveList(index, (list) => ({
                                        ...list,
                                        sections: list.sections.map((item, i) =>
                                          i === sectionIndex ? { ...item, title: event.target.value } : item),
                                      }))}
                                    />
                                  </label>
                                  <div className={styles.interactiveRows}>
                                    {section.rows.map((row, rowIndex) => (
                                      <div key={`row-${index}-${sectionIndex}-${rowIndex}`} className={styles.interactiveRowCard}>
                                        <div className={styles.interactiveRowFields}>
                                          <label className={styles.field}>
                                            {t('interactiveRowId')}
                                            <input
                                              className="text-input"
                                              value={row.id}
                                              placeholder={t('interactiveRowIdPlaceholder')}
                                              onChange={(event) => updateInteractiveList(index, (list) => ({
                                                ...list,
                                                sections: list.sections.map((item, i) => {
                                                  if (i !== sectionIndex) {
                                                    return item;
                                                  }
                                                  return {
                                                    ...item,
                                                    rows: item.rows.map((currentRow, currentRowIndex) =>
                                                      currentRowIndex === rowIndex
                                                        ? { ...currentRow, id: event.target.value }
                                                        : currentRow),
                                                  };
                                                }),
                                              }))}
                                            />
                                          </label>
                                          <label className={styles.field}>
                                            {t('interactiveRowTitle')}
                                            <input
                                              className="text-input"
                                              value={row.title}
                                              placeholder={t('interactiveRowTitlePlaceholder')}
                                              onChange={(event) => updateInteractiveList(index, (list) => ({
                                                ...list,
                                                sections: list.sections.map((item, i) => {
                                                  if (i !== sectionIndex) {
                                                    return item;
                                                  }
                                                  return {
                                                    ...item,
                                                    rows: item.rows.map((currentRow, currentRowIndex) =>
                                                      currentRowIndex === rowIndex
                                                        ? { ...currentRow, title: event.target.value }
                                                        : currentRow),
                                                  };
                                                }),
                                              }))}
                                            />
                                          </label>
                                          <label className={styles.field}>
                                            {t('interactiveRowDescription')}
                                            <input
                                              className="text-input"
                                              value={row.description ?? ''}
                                              placeholder={t('interactiveRowDescriptionPlaceholder')}
                                              onChange={(event) => updateInteractiveList(index, (list) => ({
                                                ...list,
                                                sections: list.sections.map((item, i) => {
                                                  if (i !== sectionIndex) {
                                                    return item;
                                                  }
                                                  return {
                                                    ...item,
                                                    rows: item.rows.map((currentRow, currentRowIndex) =>
                                                      currentRowIndex === rowIndex
                                                        ? { ...currentRow, description: event.target.value }
                                                        : currentRow),
                                                  };
                                                }),
                                              }))}
                                            />
                                          </label>
                                        </div>
                                        <button
                                          type="button"
                                          className="btn btn-secondary btn-sm"
                                          disabled={section.rows.length <= 1}
                                          onClick={() => updateInteractiveList(index, (list) => ({
                                            ...list,
                                            sections: list.sections.map((item, i) => {
                                              if (i !== sectionIndex) {
                                                return item;
                                              }
                                              return {
                                                ...item,
                                                rows: item.rows.filter((_, currentRowIndex) => currentRowIndex !== rowIndex),
                                              };
                                            }),
                                          }))}
                                        >
                                          {t('removeRow')}
                                        </button>
                                      </div>
                                    ))}
                                  </div>
                                  <button
                                    type="button"
                                    className="btn btn-secondary btn-sm"
                                    onClick={() => updateInteractiveList(index, (list) => ({
                                      ...list,
                                      sections: list.sections.map((item, i) =>
                                        i === sectionIndex
                                          ? { ...item, rows: [...item.rows, emptyInteractiveRow()] }
                                          : item),
                                    }))}
                                  >
                                    {t('addRow')}
                                  </button>
                                </div>
                              ))}

                              <button
                                type="button"
                                className="btn btn-secondary btn-sm"
                                onClick={() => updateInteractiveList(index, (list) => ({
                                  ...list,
                                  sections: [...list.sections, emptyInteractiveSection()],
                                }))}
                              >
                                {t('addSection')}
                              </button>
                            </div>
                          ) : (
                            <div className={styles.interactiveEditor}>
                              <label className={styles.field}>
                                {t('interactiveBody')}
                                <textarea
                                  className="text-input"
                                  rows={2}
                                  value={buttonsDraft.bodyText}
                                  placeholder={t('interactiveButtonsBodyPlaceholder')}
                                  onChange={(event) => updateInteractiveButtons(index, (buttons) => ({
                                    ...buttons,
                                    bodyText: event.target.value,
                                  }))}
                                />
                              </label>
                              <div className={styles.interactiveOptionalGrid}>
                                <label className={styles.field}>
                                  {t('interactiveHeader')}
                                  <input
                                    className="text-input"
                                    value={buttonsDraft.headerText ?? ''}
                                    placeholder={t('interactiveHeaderPlaceholder')}
                                    onChange={(event) => updateInteractiveButtons(index, (buttons) => ({
                                      ...buttons,
                                      headerText: event.target.value,
                                    }))}
                                  />
                                </label>
                                <label className={styles.field}>
                                  {t('interactiveFooter')}
                                  <input
                                    className="text-input"
                                    value={buttonsDraft.footerText ?? ''}
                                    placeholder={t('interactiveFooterPlaceholder')}
                                    onChange={(event) => updateInteractiveButtons(index, (buttons) => ({
                                      ...buttons,
                                      footerText: event.target.value,
                                    }))}
                                  />
                                </label>
                              </div>

                              <div className={styles.interactiveRows}>
                                {buttonsDraft.buttons.map((button, buttonIndex) => (
                                  <div key={`button-${index}-${buttonIndex}`} className={styles.interactiveRowCard}>
                                    <div className={styles.interactiveRowFields}>
                                      <label className={styles.field}>
                                        {t('interactiveReplyButtonId')}
                                        <input
                                          className="text-input"
                                          value={button.id}
                                          placeholder={t('interactiveReplyButtonIdPlaceholder')}
                                          onChange={(event) => updateInteractiveButtons(index, (buttons) => ({
                                            ...buttons,
                                            buttons: buttons.buttons.map((item, i) =>
                                              i === buttonIndex ? { ...item, id: event.target.value } : item),
                                          }))}
                                        />
                                      </label>
                                      <label className={styles.field}>
                                        {t('interactiveReplyButtonTitle')}
                                        <input
                                          className="text-input"
                                          value={button.title}
                                          placeholder={t('interactiveReplyButtonTitlePlaceholder')}
                                          onChange={(event) => updateInteractiveButtons(index, (buttons) => ({
                                            ...buttons,
                                            buttons: buttons.buttons.map((item, i) =>
                                              i === buttonIndex ? { ...item, title: event.target.value } : item),
                                          }))}
                                        />
                                      </label>
                                    </div>
                                    <button
                                      type="button"
                                      className="btn btn-secondary btn-sm"
                                      disabled={buttonsDraft.buttons.length <= 1}
                                      onClick={() => updateInteractiveButtons(index, (buttons) => ({
                                        ...buttons,
                                        buttons: buttons.buttons.filter((_, i) => i !== buttonIndex),
                                      }))}
                                    >
                                      {t('removeButton')}
                                    </button>
                                  </div>
                                ))}
                              </div>
                              <button
                                type="button"
                                className="btn btn-secondary btn-sm"
                                disabled={isButtonAddDisabled}
                                onClick={() => {
                                  if (isButtonAddDisabled) {
                                    return;
                                  }
                                  updateInteractiveButtons(index, (buttons) => ({
                                    ...buttons,
                                    buttons: [...buttons.buttons, emptyInteractiveButton()],
                                  }));
                                }}
                              >
                                {t('addButton')}
                              </button>
                              <p className={styles.hint}>
                                {interactiveButtonMaxCount == null
                                  ? t('interactiveButtonsNoLimitHint')
                                  : t('interactiveButtonsMaxHint', { count: interactiveButtonMaxCount })}
                              </p>
                            </div>
                          )}
                        </div>
                      ) : null}
                    </div>
                  </div>
                );
              })}
            </div>
            <div className={styles.formActions}>
              <button type="button" className="btn btn-secondary btn-sm" onClick={addAction}>
                {t('addAction')}
              </button>
            </div>
          </div>

          {saveError ? <div className="error" style={{ marginTop: '0.85rem' }}>{saveError}</div> : null}

          <div className={styles.formActionsSplit}>
            <button type="button" className="btn btn-secondary btn-sm" onClick={closeEditor} disabled={saving}>
              {t('cancel')}
            </button>
            <button type="button" className="btn btn-primary" onClick={() => void handleSave()} disabled={saving}>
              {saving ? t('saving') : t('save')}
            </button>
          </div>
        </section>
      ) : null}

      <section className={styles.sectionCard}>
        <div className={styles.sectionHeader}>
          <div>
            <h3 className={styles.sectionTitle}>{t('rulesTitle')}</h3>
            <p className={styles.sectionKicker}>{t('rulesHint')}</p>
          </div>
        </div>
        {loading ? (
          <Spinner />
        ) : error ? (
          <div className="error">{error}</div>
        ) : rules.length === 0 ? (
          <div className={styles.emptyState}>
            <h4 className={styles.emptyTitle}>{t('emptyTitle')}</h4>
            <p className={styles.emptyText}>{t('empty')}</p>
            {canManage && !editor ? (
              <button type="button" className="btn btn-primary btn-sm" onClick={openCreate}>
                {t('createRule')}
              </button>
            ) : null}
          </div>
        ) : (
          <div className={styles.rulesGrid}>
            {rules.map((rule) => (
              <article
                key={rule.id}
                className={`${styles.ruleCard} ${rule.isEnabled ? '' : styles.ruleCardDisabled}`}
              >
                <div className={styles.ruleTop}>
                  <div>
                    <h4 className={styles.ruleTitle}>{rule.name}</h4>
                    <div className={styles.ruleMetaRow}>
                      <span
                        className={`${styles.channelBadge} ${
                          rule.channel === 'messenger'
                            ? styles.channelBadgeMessenger
                            : styles.channelBadgeInstagram
                        }`}
                      >
                        {rule.channel === 'messenger' ? t('channelMessenger') : t('channelInstagram')}
                      </span>
                      <span className={styles.metaChip}>
                        {t('priorityShort', { value: rule.priority })}
                      </span>
                      <span className={styles.metaChip}>
                        {rule.conditionLogic === 'all' ? t('matchAllShort', { ns: 'automations' }) : t('matchAnyShort', { ns: 'automations' })}
                      </span>
                      <span className={`${styles.badge} ${rule.isEnabled ? styles.badgeOn : styles.badgeOff}`}>
                        {rule.isEnabled ? t('enabledLabel') : t('disabledLabel')}
                      </span>
                    </div>
                  </div>
                </div>

                <div className={styles.keywords}>
                  {rule.conditions.map((condition) => (
                    condition.type === 'ai' ? (
                      <span key={`${rule.id}-${condition.id}`} className={styles.aiChip}>
                        {t('aiChip', { ns: 'automations', question: condition.question, confidence: condition.minConfidence })}
                      </span>
                    ) : (
                      condition.keywords.map((keyword) => (
                        <span key={`${rule.id}-${condition.id}-${keyword}`} className={styles.keywordChip}>{keyword}</span>
                      ))
                    )
                  ))}
                </div>

                <div className={styles.flowRow} aria-label={t('flowLabel')}>
                  {rule.actions.map((action, index) => (
                    <React.Fragment key={`${rule.id}-flow-${index}`}>
                      {index > 0 ? <span className={styles.flowArrow} aria-hidden="true">→</span> : null}
                      <span className={styles.flowStep}>{describeAction(action)}</span>
                    </React.Fragment>
                  ))}
                </div>

                <div className={styles.ruleFooter}>
                  <div className={styles.ruleFootNote}>
                    {rule.externalContentId ? t('specificPost') : t('allPosts')}
                    <div className={styles.muted}>{renderActionSummary(rule)}</div>
                  </div>
                  {canManage ? (
                    <div className={styles.actionsCell}>
                      <button type="button" className="btn btn-secondary btn-sm" onClick={() => openEdit(rule)}>
                        {t('edit')}
                      </button>
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        onClick={() => void handleToggleEnabled(rule)}
                      >
                        {rule.isEnabled ? t('disable') : t('enable')}
                      </button>
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        onClick={() => void handleDelete(rule)}
                      >
                        {t('delete')}
                      </button>
                    </div>
                  ) : (
                    <span className={styles.muted}>{t('readOnly')}</span>
                  )}
                </div>
              </article>
            ))}
          </div>
        )}
      </section>
    </div>
  );
};
